import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import classNames from 'classnames';

import { Modal } from 'sa-ui';

import messages from './simulatedCorrectionManualStudentSelectionModalMessages';

import './SimulatedCorrectionManualStudentSelectionModal.scss';

const SimulatedCorrectionManualStudentSelectionModal = (props) => {
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showOverwriteConfirm, setShowOverwriteConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const studentsSorted = [...(props.students || [])].sort((a, b) =>
    (a.studentName || '').localeCompare(b.studentName || ''),
  );

  const hasResults = (student) =>
    student.corrected === '1' || student.corrected === '2';

  const handleContinue = async () => {
    if (!selectedStudent) return;
    if (hasResults(selectedStudent)) {
      setShowOverwriteConfirm(true);
      return;
    }
    await doReupload(selectedStudent);
  };

  const handleConfirmOverwrite = async () => {
    if (!selectedStudent) return;
    setShowOverwriteConfirm(false);
    await doReupload(selectedStudent, true);
  };

  const doReupload = async (student, confirmOverwrite = false) => {
    setSubmitting(true);
    try {
      const result = await props.onReupload(
        props.file,
        student.idUser,
        props.scannedResults,
        confirmOverwrite,
      );
      if (result && result.success) {
        props.onSuccess();
      } else if (result && result.confirmOverwrite) {
        setShowOverwriteConfirm(true);
      } else {
        props.onError((result && result.message) || props.intl.formatMessage(messages.errorGeneric));
        handleClose();
      }
    } catch (err) {
      props.onError((err && err.message) || props.intl.formatMessage(messages.errorGeneric));
      handleClose();
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setShowOverwriteConfirm(false);
    setSelectedStudent(null);
    props.onClose();
  };

  const buttons = [
    { label: props.intl.formatMessage(messages.back), onClick: handleClose },
    {
      label: submitting
        ? props.intl.formatMessage(messages.loading)
        : props.intl.formatMessage(messages.continue),
      onClick: handleContinue,
      primary: true,
      disabled: !selectedStudent || submitting,
    },
  ];

  return (
    <>
      <Modal
        show={props.show}
        onClose={handleClose}
        title={props.intl.formatMessage(messages.title)}
        buttons={buttons}
        modalBodyStyle={{ maxWidth: '90%' }}
      >
        <div className="manual-student-selection-modal">
        <p className="mb-1">
          {props.intl.formatMessage(
            props.manyResults ? messages.instructionManyResults : messages.instruction,
          )}
        </p>
        {props.file && (
          <p className="mb-3 text-muted small">
            <i className="far fa-file-alt mr-1" />
            {props.file.name}
          </p>
        )}
        <div className="table-responsive">
          <table className="table table-hover">
            <thead>
              <tr>
                <th>{props.intl.formatMessage(messages.studentName)}</th>
                <th>{props.intl.formatMessage(messages.status)}</th>
              </tr>
            </thead>
            <tbody>
              {studentsSorted.map((student) => (
                <tr
                  key={student.idUser}
                  className={classNames({
                    'table-primary': selectedStudent && selectedStudent.idUser === student.idUser,
                  })}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedStudent(student)}
                >
                  <td>
                    {student.registration} - {student.studentName}
                  </td>
                  <td>
                    {hasResults(student) ? (
                      props.intl.formatMessage(messages.statusSent)
                    ) : (
                      props.intl.formatMessage(messages.statusPending)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </div>
      </Modal>
      <Modal
        show={showOverwriteConfirm}
        onClose={() => setShowOverwriteConfirm(false)}
        title={props.intl.formatMessage(messages.overwriteTitle)}
        buttons={[
          {
            label: props.intl.formatMessage(messages.cancel),
            onClick: () => setShowOverwriteConfirm(false),
          },
          {
            label: props.intl.formatMessage(messages.confirmOverwrite),
            onClick: handleConfirmOverwrite,
            primary: true,
            disabled: submitting,
          },
        ]}
      >
        <p>
          {props.intl.formatMessage(messages.overwriteMessage, {
            studentName: (selectedStudent && selectedStudent.studentName) || '',
          })}
        </p>
      </Modal>
    </>
  );
};

SimulatedCorrectionManualStudentSelectionModal.propTypes = {
  show: PropTypes.bool.isRequired,
  students: PropTypes.arrayOf(
    PropTypes.shape({
      idUser: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      studentName: PropTypes.string,
      registration: PropTypes.string,
      corrected: PropTypes.oneOf(['0', '1', '2', null]),
    }),
  ),
  file: PropTypes.object,
  scannedResults: PropTypes.any,
  manyResults: PropTypes.bool,
  onReupload: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

SimulatedCorrectionManualStudentSelectionModal.defaultProps = {
  students: [],
  file: null,
  scannedResults: null,
  manyResults: false,
};

export default injectIntl(SimulatedCorrectionManualStudentSelectionModal);
